import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useCurrentSpace } from '../context/authHooks';

// Helper function to dynamically update the favicon
const updateFavicon = (iconSrc, defaultIcon) => {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
    }
    
    const finalDefault = defaultIcon || '/vite.svg';

    // Si no hay icono, o es 'file'/'folder', usamos el logo del espacio o del sistema
    if (!iconSrc || iconSrc === 'file' || iconSrc === 'folder') {
        link.href = finalDefault;
        if (finalDefault.endsWith('.svg')) link.type = 'image/svg+xml';
        else link.removeAttribute('type');
        return;
    }

    // Detectar si el icono es un emoji (heuristic)
    const isEmoji = /[\p{Extended_Pictographic}]/u.test(iconSrc) || (iconSrc.length <= 4 && iconSrc.codePointAt(0) > 255);
    
    if (isEmoji) {
        link.href = `data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><text y=".9em" font-size="90">${iconSrc}</text></svg>`;
        link.type = 'image/svg+xml';
    } else {
        // Es una URL o ruta de imagen
        link.href = iconSrc;
        if (iconSrc.endsWith('.png')) link.type = 'image/png';
        else if (iconSrc.endsWith('.jpg') || iconSrc.endsWith('.jpeg')) link.type = 'image/jpeg';
        else if (iconSrc.endsWith('.ico')) link.type = 'image/x-icon';
        else if (iconSrc.endsWith('.svg')) link.type = 'image/svg+xml';
        else link.removeAttribute('type');
    }
};

const PageTitleUpdater = () => {
    const location = useLocation();
    // 03.1-auth-store.md §6 grupo 2 -- solo usa `currentSpace.name`/`.icon`, calce exacto con
    // `useCurrentSpace()`.
    const { currentSpace } = useCurrentSpace();

    useEffect(() => {
        const path = location.pathname;
        const spaceName = currentSpace?.name || 'Colonny';
        const spaceIcon = currentSpace?.icon || '/vite.svg';

        // Root
        if (path === '/' || path === '') {
            document.title = spaceName;
            updateFavicon(null, spaceIcon);
            return;
        }

        // Exact matches
        if (path === '/calendar') {
            document.title = 'calendar';
            updateFavicon(null, spaceIcon);
            return;
        }
        if (path === '/docs') {
            document.title = 'docs';
            updateFavicon(null, spaceIcon);
            return;
        }
        if (path === '/database') {
            document.title = 'data';
            updateFavicon(null, spaceIcon);
            return;
        }
        if (path === '/profile') {
            document.title = 'Profile';
            updateFavicon(null, spaceIcon);
            return;
        }

        // Dynamic Docs
        const docMatch = path.match(/^\/docs\/([a-zA-Z0-9-]+)$/);
        if (docMatch) {
            const docId = docMatch[1];
            
            // Fetch only title. Identidad vía cookie de sesión, no header manual.
            fetch(`/api/docs/${docId}`, { credentials: 'include' })
                .then(res => res.json())
                .then(data => {
                    if (data && data.doc) {
                        const title = data.doc.title || data.doc.name;
                        if (title) {
                            document.title = title;
                        } else {
                            document.title = 'Documento';
                        }
                        updateFavicon(data.doc.icon, spaceIcon);
                    } else {
                        document.title = 'Documento';
                        updateFavicon(null, spaceIcon);
                    }
                })
                .catch(() => {
                    document.title = 'Documento';
                    updateFavicon(null, spaceIcon);
                });
            return;
        }

        // Default
        document.title = spaceName;
        updateFavicon(null, spaceIcon);

    }, [location, currentSpace]);

    return null;
};

export default PageTitleUpdater;
